import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Ban, ShieldAlert, ArrowLeft } from "lucide-react";
import { OfferPicker } from "@/components/OfferPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, errMsg, eur, dateFr } from "@/lib/api";
import { PageShell, Photo, Empty, Pill } from "@/components/common";
import { ReportDialog } from "@/components/ReportDialog";
import { useMe } from "@/context/MeContext";

function Bubble({ m, mine, isSeller, onOffer }) {
  if (m.type === "system") return <p className="text-center text-xs text-stone-500 my-2" data-testid="system-message">{m.text}</p>;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-forest text-sand rounded-br-sm" : "bg-white border border-stone-200 rounded-bl-sm"}`} data-testid={`message-${m.id}`}>
        {m.type === "offer" ? (
          <div className="space-y-2">
            <p className="font-semibold">Offre : {eur(m.offer_amount)}</p>
            {m.text && <p>{m.text}</p>}
            <Pill tone={m.offer_status === "accepted" ? "green" : m.offer_status === "declined" ? "red" : "warn"}>{m.offer_status === "accepted" ? "Acceptée" : m.offer_status === "declined" ? "Refusée" : "En attente"}</Pill>
            {isSeller && m.offer_status === "pending" && (
              <div className="flex gap-2">
                <button data-testid={`offer-accept-${m.id}`} onClick={() => onOffer(m.id, true)} className="rounded-full bg-emerald-600 text-white px-3 py-1 text-xs">Accepter</button>
                <button data-testid={`offer-decline-${m.id}`} onClick={() => onOffer(m.id, false)} className="rounded-full bg-stone-200 px-3 py-1 text-xs">Refuser</button>
              </div>
            )}
          </div>
        ) : <p className="whitespace-pre-line">{m.text}</p>}
        {m.flagged && <p className="text-[11px] mt-1 opacity-80 flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Lien masqué par sécurité</p>}
        <p className="text-[10px] opacity-60 mt-1">{dateFr(m.created_at)}</p>
      </div>
    </div>
  );
}

function Thread({ convId, onBack, reloadList }) {
  const api = useApi();
  const { me } = useMe();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);

  const load = useCallback(() => api.get(`/conversations/${convId}/messages`).then((r) => setData(r.data)), [api, convId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  if (!data) return <div className="p-6 text-stone-500">Chargement…</div>;
  const conv = data.conversation;
  const isSeller = conv.seller_id === me?.user_id;
  const blocked = conv.blocked_by.length > 0;

  const send = async (offerAmount = null) => {
    try {
      const { data: m } = await api.post(`/conversations/${convId}/messages`, { text: offerAmount ? "" : text, offer_amount: offerAmount });
      if (m.warning) toast.warning(m.warning);
      if (offerAmount) toast.success(`Offre de ${eur(offerAmount)} envoyée`); else setText("");
      load(); reloadList();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleBlock = async () => {
    const mine = conv.blocked_by.includes(me?.user_id);
    await api.post(`/conversations/${convId}/${mine ? "unblock" : "block"}`);
    toast.success(mine ? "Conversation débloquée" : "Conversation bloquée");
    load();
  };

  return (
    <div className="flex flex-col h-[70vh]" data-testid="message-thread">
      <div className="flex items-center gap-3 p-4 border-b border-stone-200">
        <button className="md:hidden" onClick={onBack} aria-label="Retour" data-testid="thread-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <Link to={`/annonce/${conv.listing_id}`} className="font-medium text-forest hover:underline flex-1 truncate" data-testid="thread-listing-link">Voir l'annonce</Link>
        {conv.agreed_price && <Pill tone="green">Prix convenu : {eur(conv.agreed_price)}</Pill>}
        <ReportDialog targetType="conversation" targetId={convId} label="Signaler" testid="report-conversation-btn" />
        <button data-testid="block-conversation-btn" onClick={toggleBlock} className="text-xs text-stone-500 hover:text-red-600 flex items-center gap-1"><Ban className="w-3.5 h-3.5" />{conv.blocked_by.includes(me?.user_id) ? "Débloquer" : "Bloquer"}</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand/50">
        <p className="text-[11px] text-center text-stone-500 bg-white rounded-xl p-2 border border-stone-200">Ne payez jamais hors de la plateforme et ne cliquez pas sur des liens reçus d'inconnus.</p>
        {data.messages.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === me?.user_id} isSeller={isSeller} onOffer={async (id, accept) => { try { await api.post(`/messages/${id}/offer-response`, { accept }); load(); } catch (e) { toast.error(errMsg(e)); } }} />)}
        <div ref={end} />
      </div>
      {blocked ? <p className="p-4 text-sm text-center text-stone-500" data-testid="thread-blocked-msg">Conversation bloquée.</p> : (
        <div className="p-3 border-t border-stone-200 space-y-2">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) send(); }}>
            <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message…" className="bg-white" />
            <Button data-testid="message-send-btn" type="submit" className="rounded-full bg-forest hover:bg-forest/90"><Send className="w-4 h-4" /></Button>
          </form>
          {!isSeller && data.listing?.type === "sale" && data.listing.status === "active" && <OfferPicker price={data.listing.price} onSend={send} />}
        </div>
      )}
    </div>
  );
}

export default function Messages() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState(null);
  const active = params.get("c");
  const load = useCallback(() => api.get("/conversations").then((r) => setConvs(r.data)), [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <PageShell eyebrow="Messagerie protégée" title="Messages">
      {convs && !convs.length ? <Empty testid="messages-empty">Aucune conversation. Contactez un vendeur depuis une annonce.</Empty> : (
        <div className="grid md:grid-cols-12 bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className={`md:col-span-4 border-r border-stone-200 max-h-[70vh] overflow-y-auto ${active ? "hidden md:block" : ""}`} data-testid="conversation-list">
            {(convs || []).map((c) => (
              <button key={c.id} data-testid={`conversation-item-${c.id}`} onClick={() => setParams({ c: c.id })} className={`w-full text-left flex gap-3 p-4 border-b border-stone-100 hover:bg-sand transition-colors ${active === c.id ? "bg-muted" : ""}`}>
                <Photo src={c.listing?.photos?.[0]} alt="" className="w-12 h-12 rounded-xl shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{c.listing?.title}</p>
                  <p className="text-xs text-stone-500 truncate">{c.other.display_name} · {c.last_message}</p>
                </div>
              </button>
            ))}
          </div>
          <div className={`md:col-span-8 ${active ? "" : "hidden md:block"}`}>
            {active ? <Thread key={active} convId={active} onBack={() => setParams({})} reloadList={load} /> : <div className="p-10 text-stone-500 text-sm">Sélectionnez une conversation.</div>}
          </div>
        </div>
      )}
    </PageShell>
  );
}
